import React, { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import FormButton from '@app/component/form/button/FormButton';
import Tabs from '@app/component/tabs/Tabs';
import InfoTooltip from '@ui/tooltip/InfoTooltip';
import Utilities from '@util/Utilities';
import { CHART_HEIGHT, ChartSize } from './chartTokens';
import ChartMessage from './ChartMessage';

// The frame of every chart: the title, the period, the states, and the switch between the chart
// and its table. The look is the small Portlet: a white card with a shadow.
// state:
// - default: the chart (children).
// - loading: the first load. A gray block with the height of the chart pulses.
// - refreshing: a new load with an old chart. The old chart stays at half opacity, so the page does
//   not jump.
// - empty: the query found no data. emptyLabel in a box with the height of the chart.
// - error: the load failed. errorLabel and, with onRetry, a "Try again" button.
// Give `size` the same value as the chart, so each state keeps the height of the chart.
// With `table` (usually a DataTable from lineChartTable or barChartTable), the header shows
// Chart | Table. The table view is the second way to read each value, without a pointer.
// The card knows nothing about the data: the caller gives the chart, the table and the texts.

export type ChartCardState = 'default' | 'loading' | 'refreshing' | 'empty' | 'error';

type Props = {
	title: string,
	// The period of the data, for example "Next 90 nights · 13 Oct – 10 Jan".
	period?: string,
	// An (i) after the title with the definition of the chart.
	info?: { title: string, content: React.ReactNode },
	state?: ChartCardState,
	// The size of the chart inside: small 200 px, normal 280 px, large 360 px.
	size?: ChartSize,
	emptyLabel?: string,
	errorLabel?: string,
	onRetry?: () => void,
	// The table view of the chart. It adds the Chart | Table switch.
	table?: React.ReactNode,
	// The view at the start. Default 'chart'.
	defaultView?: 'chart' | 'table',
	// A short note under the chart: the source, the time of the last scan.
	caption?: React.ReactNode,
	// More controls on the right of the header.
	actions?: React.ReactNode,
	children: React.ReactNode,
};

const VIEWS = [
	{ value: 'chart', label: 'Chart' },
	{ value: 'table', label: 'Table' },
];

const ChartCard: React.FunctionComponent<Props> = ({
	title, period, info, state = 'default', size = 'normal', emptyLabel = 'No data for this period.',
	errorLabel = 'The data did not load.', onRetry, table, defaultView = 'chart', caption, actions, children,
}) => {
	const [view, setView] = useState<string>(defaultView);
	const height = CHART_HEIGHT[size];
	const hasData = state === 'default' || state === 'refreshing';
	const showSwitch = table !== undefined && hasData;
	const showTable = showSwitch && view === 'table';

	return <section aria-busy={state === 'loading' || state === 'refreshing'} className="rounded-lg bg-white p-4 shadow">
		<div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
			<div className="min-w-0">
				<h2 className="flex items-center gap-x-1 text-base font-semibold text-gray-900">
					<span className="truncate">{title}</span>
					{info !== undefined && <InfoTooltip label={`About ${title}`} title={info.title}>{info.content}</InfoTooltip>}
				</h2>
				{period !== undefined && <p className="mt-0.5 text-xs text-gray-500">{period}</p>}
			</div>
			{(showSwitch || actions !== undefined) && <div className="flex items-center gap-x-3">
				{actions}
				{showSwitch && <Tabs<string> variant="pill" label={`View of ${title}`} options={VIEWS} value={view} onChange={setView} />}
			</div>}
		</div>

		{state === 'loading' && <div className="animate-pulse rounded-md bg-gray-100" style={{ height }}>
			<span className="sr-only">Loading</span>
		</div>}

		{state === 'empty' && <ChartMessage height={height}>{emptyLabel}</ChartMessage>}

		{state === 'error' && <ChartMessage height={height}>
			<p role="alert" className="inline-flex items-center gap-x-1.5 text-gray-700">
				<ExclamationTriangleIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-red-600" />
				{errorLabel}
			</p>
			{onRetry !== undefined && <FormButton size="small" state="white" onClick={onRetry}>Try again</FormButton>}
		</ChartMessage>}

		{hasData && <div className={Utilities.classNames('transition-opacity', state === 'refreshing' ? 'opacity-50' : '')}>
			{showTable ? table : children}
		</div>}

		{caption !== undefined && <p className="mt-3 text-xs text-gray-500">{caption}</p>}
	</section>;
};

export default ChartCard;
